import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { App, Button, Dropdown, Input, Select, Space } from "antd";
import type { TableProps } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type Key,
  type MouseEvent,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CustomButton,
  CustomSvgIcon,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import {
  CUSTOMER_STATUS_CONFIG,
  CUSTOMER_STATUS_OPTIONS,
  customerDataIndexToColumnId,
} from "../constant";
import {
  useMutationDeleteCustomer,
  useMutationBulkDeleteCustomers,
  useMutationPatchCustomerStatus,
} from "src/queries/customers.queries";
import type { Customer, CustomerStatusAction } from "src/services/customerService";
import CustomSharedInput from "../../../../sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import CustomSharedConfirmationModal from "../../../../sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import {
  ActiveIcon,
  BlockedIcon,
  CrossIcon,
  DeleteIcon,
  EditIcon,
  FilterIcon,
  MenuIcon,
  SearchIcon,
} from "src/assets/svgs";

interface CustomersPagination {
  page: number;
  size: number;
  total?: number;
}

interface CustomersSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

interface CustomersTableProps {
  data: Customer[];
  stats: {
    all: number;
    active: number;
    inactive: number;
    blocked: number;
  };
  activeStatus: string;
  onStatusFilterChange: (status: string) => void;
  isLoading?: boolean;
  pagination: CustomersPagination;
  setPagination: Dispatch<SetStateAction<{ page: number; size: number }>>;
  sorting: CustomersSorting;
  setSorting: Dispatch<SetStateAction<CustomersSorting>>;
  setSearch: Dispatch<SetStateAction<string>>;
  columnFilters: Record<string, string>;
  hasFilters: boolean;
  onColumnSearchApply: (column: string, value: unknown) => void;
  onResetFilters: () => void;
  selectedRowKeys: Key[];
  refetchList: () => Promise<void>;
  onEditCustomer: (record: Customer) => void;
}

const getCustomerState = (record: Customer) =>
  String(
    typeof record.status === "object"
      ? record.status?.name
      : (record.state ?? record.status ?? ""),
  ).toLowerCase();

const getCustomerName = (record: Customer) => {
  const fullName = [record.name, record.surname].filter(Boolean).join(" ");
  return record.companyName || fullName || "--";
};

const getCustomerSensors = (record: Customer) =>
  record.fuelSensorsCount ?? record.sensors ?? record.sensorCount ?? 0;

interface ColumnFilterActionsProps {
  searchLabel: string;
  resetLabel: string;
  searchButtonClassName?: string;
  resetButtonClassName?: string;
  onSearch: () => void;
  onReset: () => void;
}

const ColumnFilterActions = ({
  searchLabel,
  resetLabel,
  searchButtonClassName = "w-28 !rounded-sm",
  resetButtonClassName = "w-28 !rounded-sm",
  onSearch,
  onReset,
}: ColumnFilterActionsProps) => (
  <Space className="flex-shrink-0 h-8">
    <Button type="primary" onClick={onSearch} className={searchButtonClassName}>
      {searchLabel}
    </Button>
    <Button onClick={onReset} className={resetButtonClassName}>
      {resetLabel}
    </Button>
  </Space>
);

interface ApiTextFilterDropdownProps extends FilterDropdownProps {
  placeholder: string;
  searchLabel: string;
  resetLabel: string;
  onApply: (value: unknown) => void;
  onResetSearch: () => void;
}

const ApiTextFilterDropdown = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  placeholder,
  searchLabel,
  resetLabel,
  onApply,
  onResetSearch,
}: ApiTextFilterDropdownProps) => {
  const applyFilter = () => {
    onApply(selectedKeys[0]);
    confirm();
  };

  const resetFilter = () => {
    clearFilters?.();
    onResetSearch();
    confirm();
  };

  return (
    <div className="p-2">
      <Input
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={(event) =>
          setSelectedKeys(event.target.value ? [event.target.value] : [])
        }
        onPressEnter={applyFilter}
        className="!mb-2 !block !rounded-sm"
      />
      <ColumnFilterActions
        searchLabel={searchLabel}
        resetLabel={resetLabel}
        onSearch={applyFilter}
        onReset={resetFilter}
      />
    </div>
  );
};

interface ApiSelectFilterDropdownProps extends FilterDropdownProps {
  placeholder: string;
  options: Array<{ label: string; value: string }>;
  searchLabel: string;
  resetLabel: string;
  onApply: (value: unknown) => void;
  onResetSearch: () => void;
}

const ApiSelectFilterDropdown = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  placeholder,
  options,
  searchLabel,
  resetLabel,
  onApply,
  onResetSearch,
}: ApiSelectFilterDropdownProps) => {
  const applyFilter = () => {
    onApply(selectedKeys[0]);
    confirm();
  };

  const resetFilter = () => {
    clearFilters?.();
    onResetSearch();
    confirm();
  };

  return (
    <div className="w-[220px] p-2">
      <Select
        allowClear
        className="w-full !mb-2 filter-select"
        placeholder={placeholder}
        value={(selectedKeys[0] as string | undefined) ?? undefined}
        options={options}
        onChange={(value) => {
          setSelectedKeys(value ? [value] : []);
        }}
      />
      <ColumnFilterActions
        searchLabel={searchLabel}
        resetLabel={resetLabel}
        searchButtonClassName="w-[98px] !rounded-sm"
        resetButtonClassName="w-[98px] !rounded-sm"
        onSearch={applyFilter}
        onReset={resetFilter}
      />
    </div>
  );
};

const statCardClassName =
  "min-h-[124px] w-full min-w-0 rounded-[8px] border px-5 py-[18px]";
const activeStatCardClassName = "shadow-[0_4px_8px_rgba(0,0,0,0.35)]";
const statLabelClassName = "!mb-3.5 !block !text-[15.75px] !font-[600] !text-black";
const statValueClassName = "!text-[31.8px] !font-[700] leading-none";
const customerCellStrongClassName = "text-black !font-[600] !text-[14px]";

const customerStatCards = [
  {
    key: "ALL",
    labelKey: "all_customers",
    valueKey: "all",
    borderClassName: "border-primary",
    idleClassName: "bg-white",
    activeClassName: "bg-primary",
    labelClassName: "text-black",
    valueClassName: "!text-primary",
    activeLabelClassName: "!text-white",
    activeValueClassName: "!text-white",
  },
  {
    key: "ACTIVE",
    labelKey: "active_customers",
    valueKey: "active",
    borderClassName: "border-green2",
    idleClassName: "bg-[#edf9f3]",
    activeClassName: "bg-green2",
    labelClassName: "text-black",
    valueClassName: "!text-green2",
    activeLabelClassName: "!text-white",
    activeValueClassName: "!text-white",
  },
  {
    key: "INACTIVE",
    labelKey: "inactive_customers",
    valueKey: "inactive",
    borderClassName: "border-purple",
    idleClassName: "bg-[#f7f2ff]",
    activeClassName: "bg-purple",
    labelClassName: "text-black",
    valueClassName: "!text-purple",
    activeLabelClassName: "!text-white",
    activeValueClassName: "!text-white",
  },
  {
    key: "BLOCKED",
    labelKey: "blocked_customers",
    valueKey: "blocked",
    borderClassName: "border-red-shade",
    idleClassName: "bg-[#fff6f8]",
    activeClassName: "bg-red-shade",
    labelClassName: "text-black",
    valueClassName: "!text-red",
    activeLabelClassName: "!text-white",
    activeValueClassName: "!text-white",
  },
] as const;

const StatusBadge = ({ record }: { record: Customer }) => {
  const { t } = useTranslation();
  const state = getCustomerState(record);
  const statusConfig = CUSTOMER_STATUS_CONFIG[state];

  if (!statusConfig) {
    return <span className="text-sm text-black">--</span>;
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-black">
      {state === "active" ?<ActiveIcon
        className="!cursor-default w-[10px] h-[10px]"
      />:state === "blocked" ?
      <BlockedIcon
        className="!cursor-default w-[10px] h-[10px]"
      />:state === "inactive" ? (
        <span className="h-[10px] w-[10px] rounded-full bg-gray6" />
      ) : null}
      {t(statusConfig.labelKey)}
    </span>
  );
};

const CustomerActionMenu = ({
  record,
  onStatusAction,
  activeLoading,
  inactiveLoading,
  blockLoading,
}: {
  record: Customer;
  onStatusAction: (
    record: Customer,
    status: CustomerStatusAction,
  ) => Promise<void>;
  activeLoading: boolean;
  inactiveLoading: boolean;
  blockLoading: boolean;
}) => {
  const { t } = useTranslation();
  const state = getCustomerState(record);

  const handleStatusAction = async (
    event: MouseEvent<HTMLElement>,
    status: CustomerStatusAction,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    await onStatusAction(record, status);
  };

  return (
    <div className="min-w-[216px] overflow-hidden rounded-sm bg-white shadow-[0_6px_16px_rgba(0,0,0,0.16)]">
      {state !== "active" && (
        <Button
          type="text"
          loading={activeLoading}
          disabled={inactiveLoading || blockLoading}
          className="!flex !items-center !justify-start !cursor-pointer !h-[36px] !w-full !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!outline-none"
          onClick={(event) => handleStatusAction(event, "ACTIVATE")}
        >
          {t("active_customer")}
        </Button>
      )}
      {state !== "inactive" && (
        <Button
          type="text"
          loading={inactiveLoading}
          disabled={activeLoading || blockLoading}
          className="!flex !items-center !justify-start !cursor-pointer !h-[36px] !w-full !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!outline-none"
          onClick={(event) => handleStatusAction(event, "INACTIVE")}
        >
          {t("inactive_customer")}
        </Button>
      )}
      {state !== "blocked" && (
        <Button
          type="text"
          loading={blockLoading}
          disabled={activeLoading || inactiveLoading}
          className="!flex !items-center !justify-start !cursor-pointer !h-[36px] !w-full !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-red hover:!bg-primary6 focus:!outline-none"
          onClick={(event) => handleStatusAction(event, "BLOCKED")}
        >
          {t("block_customer")}
        </Button>
      )}
    </div>
  );
};

const CustomersTable = ({
  data,
  stats,
  activeStatus,
  onStatusFilterChange,
  isLoading = false,
  pagination,
  setPagination,
  sorting,
  setSorting,
  setSearch,
  columnFilters,
  hasFilters,
  onColumnSearchApply,
  onResetFilters,
  selectedRowKeys,
  refetchList,
  onEditCustomer,
}: CustomersTableProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [topSearchValue, setTopSearchValue] = useState("");
  const [deleteIds, setDeleteIds] = useState<Array<string | number> | null>(
    null,
  );
  const [statusActionLoading, setStatusActionLoading] = useState<{
    id: Customer["id"];
    status: CustomerStatusAction;
  } | null>(null);
  const { mutateAsync: deleteCustomer, isPending: isDeleteLoading } =
    useMutationDeleteCustomer();
  const { mutateAsync: bulkDeleteCustomers, isPending: isBulkDeleteLoading } =
    useMutationBulkDeleteCustomers();
  const {
    mutateAsync: patchCustomerStatus,
    isPending: isPatchCustomerStatusLoading,
  } =
    useMutationPatchCustomerStatus();

  const onPaginationChange = ({ page, size }: { page: number; size: number }) => {
    setPagination({ page, size });
  };

  const onTableChange: TableProps<Customer>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra,
  ) => {
    if (extra.action === "filter") return;

    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const order = normalizedSorter?.order;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;

    if (!order || !dataIndex) {
      setSorting({ sortBy: "ID", sortDir: "DESC" });
      return;
    }

    const sortBy = customerDataIndexToColumnId[String(dataIndex)];

    if (!sortBy) {
      setSorting({ sortBy: "ID", sortDir: "DESC" });
      return;
    }

    setSorting({
      sortBy,
      sortDir: order === "ascend" ? "ASC" : "DESC",
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteIds?.length) return;

    try {
      if (deleteIds.length === 1) {
        const [deleteId] = deleteIds;
        if (deleteId === undefined) return;

        await deleteCustomer(deleteId);
        message.success(t("customer_deleted_successfully"));
      } else {
        await bulkDeleteCustomers(deleteIds);
        message.success(t("customers_deleted_successfully"));
      }

      await refetchList();
      setDeleteIds(null);
    } catch {
      message.error(
        deleteIds.length === 1
          ? t("failed_to_delete_customer")
          : t("failed_to_delete_customers"),
      );
    }
  };

  const openBulkDeleteModal = () => {
    if (selectedRowKeys.length === 0) return;

    setDeleteIds(
      selectedRowKeys.filter(
        (key): key is string | number =>
          typeof key === "string" || typeof key === "number",
      ),
    );
  };

  const handleResetFilters = () => {
    setTopSearchValue("");
    onResetFilters();
  };

  const handleCustomerStatusAction = useCallback(
    async (record: Customer, status: CustomerStatusAction) => {
      setStatusActionLoading({ id: record.id, status });
      try {
        await patchCustomerStatus({ id: record.id, status });
        const successMessageByStatus: Record<CustomerStatusAction, string> = {
          ACTIVATE: t("customer_activated_successfully"),
          INACTIVE: t("customer_inactivated_successfully"),
          BLOCKED: t("customer_blocked_successfully"),
        };
        message.success(successMessageByStatus[status]);
        await refetchList();
      } catch {
        const errorMessageByStatus: Record<CustomerStatusAction, string> = {
          ACTIVATE: t("failed_to_activate_customer"),
          INACTIVE: t("failed_to_inactivate_customer"),
          BLOCKED: t("failed_to_block_customer"),
        };
        message.error(errorMessageByStatus[status]);
      } finally {
        setStatusActionLoading(null);
      }
    },
    [message, patchCustomerStatus, refetchList, t],
  );

  const columns: TableProps<Customer>["columns"] = useMemo(
    () => [
      {
        title: t("name"),
        dataIndex: "name",
        key: "name",
        filteredValue: columnFilters.name ? [columnFilters.name] : null,
        filterDropdown: (filterProps) => (
          <ApiTextFilterDropdown
            {...filterProps}
            placeholder={`${t("search")} ${t("name")}`}
            searchLabel={t("search")}
            resetLabel={t("reset")}
            onApply={(value) => {
              setTopSearchValue("");
              onColumnSearchApply("name", value);
            }}
            onResetSearch={handleResetFilters}
          />
        ),
        filterIcon: () => (
          <FilterIcon
            className="customers-column-search-icon w-[11px] h-[11px]"
          />
        ),
        render: (_, record) => (
          <CustomTypography.CustomText className={customerCellStrongClassName}>
            {getCustomerName(record)}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("type"),
        dataIndex: "type",
        key: "type",
        sorter: true,
        render: (_, record) => (
          <CustomTypography.CustomText className={customerCellStrongClassName}>
            {record.clientType?.name || record.type || "--"}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("customer_manager"),
        dataIndex: "customerManager",
        key: "customerManager",
        sorter: true,
        render: (_, record) => (
          <CustomTypography.CustomText className={customerCellStrongClassName}>
            {record.clientManager?.name ||
              record.customerManager ||
              record.managerName ||
              "--"}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("mail"),
        dataIndex: "mail",
        key: "mail",
        sorter: true,
        render: (_, record) => record.mail || record.email || "--",
      },
      {
        title: t("devices"),
        dataIndex: "devices",
        key: "devices",
        sorter: true,
        render: (_, record) =>
          record.devicesCount ?? record.devices ?? record.deviceCount ?? 0,
      },
      {
        title: t("sensors"),
        dataIndex: "sensors",
        key: "sensors",
        filteredValue: columnFilters.sensors ? [columnFilters.sensors] : null,
        filterDropdown: (filterProps) => (
          <ApiTextFilterDropdown
            {...filterProps}
            placeholder={`${t("search")} ${t("sensors")}`}
            searchLabel={t("search")}
            resetLabel={t("reset")}
            onApply={(value) => {
              setTopSearchValue("");
              onColumnSearchApply("sensors", value);
            }}
            onResetSearch={handleResetFilters}
          />
        ),
        filterIcon: () => (
          <FilterIcon
            className="customers-column-search-icon w-[11px] h-[11px]"
          />
        ),
        render: (_, record) => getCustomerSensors(record),
      },
      {
        title: t("state"),
        dataIndex: "state",
        key: "state",
        filteredValue:
          activeStatus !== "ALL" ? [activeStatus.toLowerCase()] : null,
        filterDropdown: (filterProps) => (
          <ApiSelectFilterDropdown
            {...filterProps}
            placeholder={t("state")}
            options={CUSTOMER_STATUS_OPTIONS.map((option) => ({
              label: t(option.label),
              value: option.value.toLowerCase(),
            }))}
            searchLabel={t("search")}
            resetLabel={t("reset")}
            onApply={(value) => {
              setTopSearchValue("");
              onStatusFilterChange(String(value ?? "ALL").toUpperCase());
            }}
            onResetSearch={handleResetFilters}
          />
        ),
        filterIcon: () => (
          <FilterIcon
            className="customers-column-search-icon w-[11px] h-[11px]"
          />
        ),
        render: (_, record) => <StatusBadge record={record} />,
      },
      {
        title: t("action"),
        key: "action",
        render: (_, record) => {
          const rowId = record.id;
          const isCurrentStatusActionLoading =
            isPatchCustomerStatusLoading &&
            statusActionLoading?.id === record.id;

          return (
            <div className="flex items-center gap-2 customer-actions">
              <CustomTooltip title={t("edit")}>
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("edit_customer")}
                  icon={<EditIcon className="w-[16px] h-[16px]" />}
                  onClick={(event) => {
                    event?.stopPropagation?.();
                    onEditCustomer(record);
                  }}
                />
              </CustomTooltip>
              <CustomTooltip title={t("delete")}>
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("delete_customer")}
                  icon={<DeleteIcon className="w-[15px] h-[16px]" />}
                  onClick={(event) => {
                    event?.stopPropagation?.();
                    setDeleteIds([rowId]);
                  }}
                />
              </CustomTooltip>
              <Dropdown
                trigger={["click"]}
                dropdownRender={() => (
                  <CustomerActionMenu
                    record={record}
                    onStatusAction={handleCustomerStatusAction}
                    activeLoading={
                      isCurrentStatusActionLoading &&
                      statusActionLoading?.status === "ACTIVATE"
                    }
                    inactiveLoading={
                      isCurrentStatusActionLoading &&
                      statusActionLoading?.status === "INACTIVE"
                    }
                    blockLoading={
                      isCurrentStatusActionLoading &&
                      statusActionLoading?.status === "BLOCKED"
                    }
                  />
                )}
                placement="bottomRight"
                overlayClassName="customer-action-dropdown"
              >
                <CustomTooltip title={t("more_action")}>
                  <CustomButton
                    type="icon"
                    buttonSize="small"
                    aria-label={t("more_actions")}
                    icon={<MenuIcon className="w-[3px] h-[16px]" />}
                    className="ml-2"
                    onClick={(event) => event?.stopPropagation?.()}
                  />
                </CustomTooltip>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [
      handleCustomerStatusAction,
      handleResetFilters,
      activeStatus,
      columnFilters,
      isDeleteLoading,
      isPatchCustomerStatusLoading,
      navigate,
      onColumnSearchApply,
      onStatusFilterChange,
      setPagination,
      setSearch,
      statusActionLoading,
      t,
    ],
  );

  const records = Array.isArray(data) ? data : [];

  const handleApiSearch = (value: string) => {
    setTopSearchValue(value);
    setSearch(value);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (record: Customer) => {
    navigate(`/customers/${record.id}`, { state: { customer: record } });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 px-[18px] -mt-2 mb-1.5">
        {/* <CustomSharedInput
          allowClear={{
            clearIcon: (
              <CrossIcon 
                className="mr-0.5 customers-search-clear-icon w-[8.5px] h-[8.5px]"
              />
            ),
          }}
          prefix={
            <SearchIcon className="customers-search-icon w-[11px] h-[11px]" />
          }
          placeholder={t("search")}
          value={topSearchValue}
          className="global-filter-search-class !rounded-[4px]"
          onChange={(event) => handleApiSearch(event.target.value)}
        /> */}

         {/* <CustomSharedInput
          allowClear={{
            clearIcon: (
              <CrossIcon 
                className="mr-0.5 customers-search-clear-icon w-[8.5px] h-[8.5px]"
              />
            ),
          }}
          prefix={
            <SearchIcon className="customers-search-icon w-[14px] h-[14px]" />
          }
          placeholder={t("search")}
          value={topSearchValue}
          className="global-filter-search-class"
          onChange={(event) => handleApiSearch(event.target.value)}
        /> */}

        <CustomSharedInput
          allowClear={{
            clearIcon: (
              <CrossIcon 
                className="mr-0.5 customers-search-clear-icon w-[8.5px] h-[8.5px]"
              />
            ),
          }}
          prefix={
            <SearchIcon className="customers-search-icon w-[14px] h-[14px]" />
          }
          placeholder={t("search")}
          value={topSearchValue}
          className="global-filter-search-class !rounded-[4px]"
          onChange={(event) => handleApiSearch(event.target.value)}

        />
        {hasFilters && (
          <CustomButton
            label={t("reset")}
            buttonSize="medium"
            className="customer-detail-reset-btn !mt-5.5"
            style={{ height: 32 }}
            onClick={handleResetFilters}
          />
        )}
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3 px-[18px] pb-5 pt-[18px] max-[560px]:grid-cols-1">
        {customerStatCards.map((card) => {
          const isActive = activeStatus === card.key;

          return (
            <div
              key={card.key}
              className={`${statCardClassName} ${card.borderClassName} ${
                isActive ? card.activeClassName : card.idleClassName
              } ${
                isActive ? activeStatCardClassName : ""
              } cursor-pointer text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary4`}
              onClick={() => onStatusFilterChange(card.key)}
            >
              <CustomTypography.CustomText
                className={`${statLabelClassName} ${
                  isActive ? card.activeLabelClassName : card.labelClassName
                }`}
              >
                {t(card.labelKey)}
              </CustomTypography.CustomText>
              <CustomTypography.CustomText
                className={`${statValueClassName} ${
                  isActive ? card.activeValueClassName : card.valueClassName
                }`}
              >
                {formatNumber(stats[card.valueKey], i18n.language)}
              </CustomTypography.CustomText>
            </div>
          );
        })}
      </div>

      {selectedRowKeys.length > 0 && (
        <div className="flex items-center justify-end gap-2">
          <span className="text-[13px] text-gray8">
            {t("selected_count", { count: selectedRowKeys.length })}
          </span>
          <CustomButton
            danger
            type="secondary"
            label={t("delete")}
            onClick={openBulkDeleteModal}
          />
        </div>
      )}

      <CustomTable<Customer>
        customClass="customers-table"
        columns={columns}
        data={records}
        rowKey={(record) => record.id}
        pageSize={pagination.size}
        currentPage={pagination.page}
        totalCount={pagination.total ?? records.length}
        handlePagination={(newPage) =>
          onPaginationChange({ page: newPage, size: pagination.size })
        }
        handlePageSizeChange={(newSize) =>
          onPaginationChange({ page: 0, size: Number(newSize) })
        }
        showSearchInput={false}
        showCustomizeColumns={false}
        showDownload={false}
        sorting={sorting}
        onChange={onTableChange}
        isLoading={isLoading}
        isCustomRowClick
        customRowClick={handleRowClick}
        locale={{
          emptyText: t("no_data"),
        }}
      />

      <CustomSharedConfirmationModal
        open={!!deleteIds}
        onCancel={() => setDeleteIds(null)}
        onConfirm={handleDeleteConfirm}
        title={
          deleteIds?.length === 1
            ? t("confirm_delete_customer")
            : t("confirm_delete_selected_customers")
        }
        description={t("the_deletion_operation_is_irreversible")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        isLoading={isDeleteLoading || isBulkDeleteLoading}
        centered
      />
    </>
  );
};

export default CustomersTable;
